import React, { useCallback, useMemo, useState } from 'react';
import {
  createQColumnHelper,
  DataProvider,
  Filtering,
  Pagination,
  QBodyLayout,
  QButton,
  QDataTable,
  QEmptyState,
  QHeading,
  QSelectItem,
  QSpinner,
  QStack,
  Sorting,
  useCurrentUser,
} from '@qualio/ui-components';
import { createSearchParams, useMatch, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { Template } from '../../types';
import templateApi from '../../services/templateApi';
import { useQuery } from 'react-query';
import { getDateTimeStr, getTagText } from '../../utils/textUtil';
import { StatusFilterV2 } from './StatusFilterV2';
import { CreateTemplateModal } from '../../components/CreateTemplateModal/CreateTemplateModal';
import ActiveStatusFilter from './ActiveStatusFilter';

const columnHelper = createQColumnHelper<Template>();
const CREATE_TEMPLATE_NEW_ROUTE = '/doc-templates/new';

const filterBySearchTerm = (item: Template, searchTerm: string | undefined): boolean => {
  if (!searchTerm) return true;
  const term = searchTerm.toLowerCase();
  return item.name.toLowerCase().includes(term) || item.prefix.toLowerCase().includes(term);
};

const TemplateListView: React.FC = () => {
  const { t } = useTranslation();
  const currentUser = useCurrentUser();
  const routeMatch = useMatch(CREATE_TEMPLATE_NEW_ROUTE);
  const [searchParams] = useSearchParams();
  const [showCreateTemplateModal, setShowCreateTemplateModal] = useState(
    routeMatch?.pathnameBase === CREATE_TEMPLATE_NEW_ROUTE,
  );
  const navigate = useNavigate();

  const defaultStatus = 'allOpenStatuses';
  const [templateStatuses, setTemplateStatuses] = useState(defaultStatus);

  const shouldSortByStatus = useMemo(() => templateStatuses === defaultStatus, [templateStatuses, defaultStatus]);

  const loadTemplates = async () => {
    const templatesResponse = await templateApi.getTemplatesByCompanyIdAndStatuses(currentUser.companyId, [
      templateStatuses,
    ]);

    return templatesResponse.sort((a, z) => {
      if (!a.modified_time && !z.modified_time) return 0;
      if (!z.modified_time) return 1;
      if (!a.modified_time) return -1;
      return a.modified_time.getTime() - z.modified_time.getTime();
    });
  };

  const { data, isLoading } = useQuery({
    queryKey: ['templates', templateStatuses],
    queryFn: loadTemplates,
  });

  const navigateToPath = useCallback(
    (path: string) => {
      const page = searchParams.get('page') || '0';
      const orderBy = searchParams.get('orderBy') || '-modified_time';
      navigate({
        pathname: path,
        search: `?${createSearchParams({
          page,
          orderBy,
        })}`,
      });
    },
    [navigate, searchParams],
  );

  const handleTitleAction = useCallback(
    (clickedTemplate: Template) => {
      navigateToPath(`/doc-templates/${clickedTemplate.id}`);
    },
    [navigateToPath],
  );

  const handleCreateTemplateClick = useCallback(() => {
    setShowCreateTemplateModal(true);
  }, [setShowCreateTemplateModal]);

  const columns = useMemo(() => {
    return [
      columnHelper.id('prefix', {
        header: t('template.list.table.columns.prefix'),
        id: 'prefix',
        width: '200px',
      }),
      columnHelper.textAction('name', handleTitleAction, {
        header: t('template.list.table.columns.title'),
        id: 'name',
        minWidth: '305px',
      }),
      columnHelper.text('version', {
        header: t('template.list.table.columns.version'),
        id: 'version',
        width: '160px',
      }),
      columnHelper.text((template) => getDateTimeStr(template.modified_time), {
        header: t('template.list.table.columns.lastModified'),
        id: 'modified_time',
        width: '200px',
      }),
      columnHelper.status((template) => getTagText(template.status), {
        header: t('template.list.table.columns.status'),
        id: 'status',
        width: '200px',
        enableSorting: shouldSortByStatus,
        statuses: {
          Effective: 'green',
          Archived: 'red',
          Draft: 'gray',
          Superseded: 'red',
        },
      }),
    ];
  }, [handleTitleAction, shouldSortByStatus, t]);

  const statusValueLabelMap: Record<string, string> = useMemo(
    () => ({
      allOpenStatuses: t('template.list.filters.status.all'),
      draft: t('template.list.filters.status.draft'),
      effective: t('template.list.filters.status.effective'),
      superseded: t('template.list.filters.status.superseded'),
      archived: t('template.list.filters.status.archived'),
    }),
    [t],
  );

  const statusList = useMemo(
    () =>
      Object.keys(statusValueLabelMap).map((key) => ({ value: key, label: statusValueLabelMap[key] } as QSelectItem)),
    [statusValueLabelMap],
  );

  const statusMap = useMemo(() => {
    return statusList.reduce((acc, status) => {
      acc[status['value']] = status['label'];
      return acc;
    }, {} as Record<string, string>);
  }, [statusList]);

  const activeStatusFilterRender = useCallback(
    (value: unknown) => <ActiveStatusFilter value={value} statusMap={statusMap} />,
    [statusMap],
  );

  const filterDefinitions: Filtering.FilterDefinitions = useMemo(() => {
    return {
      status: {
        label: t('template.list.filters.status.label'),
        schema: Filtering.schemas.StringSchema().nullable(),
        activeRender: activeStatusFilterRender,
        defaultValue: defaultStatus,
      },
    };
  }, [activeStatusFilterRender, t]);

  if (isLoading) {
    return <QSpinner />;
  }

  return (
    <QBodyLayout.Default>
      <QStack isInline justify="space-between">
        <QHeading size="lg" mb={6} data-cy="templates-page-heading">
          {t('template.list.title')}
        </QHeading>
        <QButton onClick={handleCreateTemplateClick} data-cy="create-draft-template-modal">
          {t('template.list.createButton')}
        </QButton>
      </QStack>
      <Pagination.Auto pageSizeKey="size" pageIndexKey="page" clientSide>
        <Sorting.DefaultSortingProvider
          initialSort={shouldSortByStatus ? { column: 'status' } : { column: 'modified_time', descending: true }}
        >
          <Filtering.FilterProvider
            definitions={filterDefinitions}
            persistanceKey="document-templates-list-table"
            searchTermKey="search"
          >
            <DataProvider.Fixed data={data ?? []} filterOption={filterBySearchTerm}>
              <QDataTable columns={columns} data-cy="template-list-table" data-testid="template-list-table">
                <Filtering.CustomFormContent>
                  <StatusFilterV2 filterOptions={statusList} setTemplateStatuses={setTemplateStatuses} />
                </Filtering.CustomFormContent>
                <QEmptyState
                  title={t('template.list.emptyState.title')}
                  subtitle={t('template.list.emptyState.subtitle')}
                  data-cy="templates-table-empty-state"
                  data-testid="templates-table-empty-state"
                />
              </QDataTable>
            </DataProvider.Fixed>
          </Filtering.FilterProvider>
        </Sorting.DefaultSortingProvider>
      </Pagination.Auto>
      {showCreateTemplateModal ? <CreateTemplateModal onClose={setShowCreateTemplateModal} /> : null}
    </QBodyLayout.Default>
  );
};

export default TemplateListView;
